import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Table,
  Alert,
  Row,
  Col,
} from "reactstrap";

import { API_BASE_URL, buildApiUrl } from "../config/api";

const API_BASE = `${API_BASE_URL}/homevideo`;
const allowedExtensions = [".mp4", ".mov", ".m4v", ".avi", ".wmv", ".webm", ".mkv"];

const isValidVideoExtension = (fileName = "") => {
  const ext = fileName.slice(((fileName.lastIndexOf(".") - 1) >>> 0) + 2).toLowerCase();
  return allowedExtensions.includes(`.${ext}`);
};

function HomeVideo() {
  const [videos, setVideos] = useState([]);
  const [videoFile, setVideoFile] = useState(null);
  const [videoError, setVideoError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState("success");
  const [editingVideo, setEditingVideo] = useState(null);
  const [editingFile, setEditingFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API_BASE}/list`);
      if (response.data.success) {
        setVideos(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching home videos:", error);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  const handleNewFileChange = (e) => {
    const file = e.target.files[0];

    if (file && !isValidVideoExtension(file.name)) {
      setVideoError("Invalid file type. Allowed: MP4, MOV, M4V, AVI, WMV, WEBM, MKV");
      return;
    }

    setVideoError("");
    setVideoFile(file || null);
  };

  const resetNewForm = () => {
    setVideoFile(null);
    setVideoError("");
  };

  const handleAddVideo = async (event) => {
    event.preventDefault();
    if (!videoFile) {
      setVideoError("Video file is required");
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append("video", videoFile);

      await axios.post(`${API_BASE}/add`, formData);
      setStatusMessage("Video added successfully");
      setStatusType("success");
      resetNewForm();
      fetchVideos();
    } catch (error) {
      console.error("Error adding video:", error);
      setStatusMessage("Failed to add video");
      setStatusType("danger");
    } finally {
      setIsUploading(false);
    }
  };

  const openEdit = (video) => {
    setEditingVideo({ ...video });
    setEditingFile(null);
    setStatusMessage("");
  };

  const handleEditFileChange = (e) => {
    const file = e.target.files[0];

    if (file && !isValidVideoExtension(file.name)) {
      setStatusMessage("Invalid file type. Allowed: MP4, MOV, M4V, AVI, WMV, WEBM, MKV");
      setStatusType("danger");
      return;
    }

    setStatusMessage("");
    setEditingFile(file || null);
  };

  const handleUpdateVideo = async (event) => {
    event.preventDefault();
    if (!editingVideo) return;
    if (!editingFile) {
      setStatusMessage("Please select a video file to replace the current one.");
      setStatusType("danger");
      return;
    }

    setIsUpdating(true);

    try {
      const formData = new FormData();
      formData.append("video", editingFile);

      await axios.put(`${API_BASE}/update/${editingVideo.id}`, formData);
      setStatusMessage("Video updated successfully");
      setStatusType("success");
      setEditingVideo(null);
      setEditingFile(null);
      fetchVideos();
    } catch (error) {
      console.error("Error updating video:", error);
      setStatusMessage("Failed to update video");
      setStatusType("danger");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteVideo = async (id) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this video?");
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API_BASE}/delete/${id}`);
      setStatusMessage("Video deleted successfully");
      setStatusType("success");
      fetchVideos();
    } catch (error) {
      console.error("Error deleting video:", error);
      setStatusMessage("Failed to delete video");
      setStatusType("danger");
    }
  };

  const getVideoUrl = (path = "") => {
    if (!path) return "";
    return buildApiUrl(path);
  };

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Home Screen Orientation Video</CardTitle>
            </CardHeader>
            <CardBody>
              {statusMessage && (
                <Alert color={statusType} toggle={() => setStatusMessage("")}>
                  {statusMessage}
                </Alert>
              )}

              {/* <Form onSubmit={handleAddVideo}>
                <FormGroup>
                  <Label for="video">Upload video</Label>
                  <Input id="video" type="file" accept="video/*" onChange={handleNewFileChange} />
                  {videoError && <div style={{ color: "red" }}>{videoError}</div>}
                </FormGroup>
                <Button color="primary" type="submit" disabled={isUploading}>
                  {isUploading ? "Uploading..." : "Add Video"}
                </Button>
              </Form> */}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Existing Videos</CardTitle>
            </CardHeader>
            <CardBody>
              <Table responsive>
                <thead className="text-primary">
                  <tr>
                    <th>#</th>
                    <th>Preview</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {videos.length === 0 && (
                    <tr>
                      <td colSpan="3" className="text-center">
                        No videos found.
                      </td>
                    </tr>
                  )}
                  {videos.map((video, index) => (
                    <tr key={video.id}>
                      <td>{index + 1}</td>
                      <td>
                        {video.video_path && (
                          <video controls width="220">
                            <source src={getVideoUrl(video.video_path)} type="video/mp4" />
                            Your browser does not support the video tag.
                          </video>
                        )}
                      </td>
                      <td>
                        <Button color="info" size="sm" onClick={() => openEdit(video)}>
                          Replace
                        </Button>{" "}
                        <Button color="danger" size="sm" onClick={() => handleDeleteVideo(video.id)}>
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {editingVideo && (
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h4">Replace Video</CardTitle>
              </CardHeader>
              <CardBody>
                <Form onSubmit={handleUpdateVideo}>
                  <FormGroup>
                    <Label for="edit_video">Select a new video file</Label>
                    <Input id="edit_video" type="file" accept="video/*" onChange={handleEditFileChange} />
                  </FormGroup>
                  <Button color="primary" type="submit" disabled={isUpdating}>
                    {isUpdating ? "Saving..." : "Save Changes"}
                  </Button>{" "}
                  <Button color="secondary" type="button" onClick={() => setEditingVideo(null)}>
                    Cancel
                  </Button>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
}

export default HomeVideo;
